import { useGoogleLogin } from '@react-oauth/google';
import * as React from 'react';
import FacebookLogin from 'react-facebook-login';
import { useDispatch, useSelector } from 'react-redux';

import AppleLogin from '@Components/AppleLogin/AppleLogin';
import Button from '@Components/Buttons/Button';
import Icon, { IconSizes } from '@Components/Icon';
import Typography from '@Components/Typography';
import config, { NodeEnvEnum } from '@Config/config';
import { WebViewMessagesTypes } from '@Config/constants';
import { Messages } from '@Config/messages';
import { AppleLoginType } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { getAppleAppId, getAppleRedirectUri, getFbAppId } from '@Store/app/app.selectors';
import { loginWithApple, loginWithFacebook, loginWithGoogle } from '@Store/auth/auth.actions';
import { AppleAuthResponse, FacebookAuthResponse, FacebookAuthResponseStatus } from '@Store/auth/auth.types';
import useWebViewListener from '@Utils/hooks/useWebViewListener';
import { isMobileApp, postMessageToWebView } from '@Utils/mobileWebView';

import styles from './SocialLogins.module.scss';

interface SocialLoginsProps {
  referer?: string;
  addNaturalCompany?: boolean;
}

const SocialLogins: React.FunctionComponent<SocialLoginsProps> = ({ referer, addNaturalCompany = false }) => {
  const dispatch = useDispatch();
  const t = useTranslations();
  const fbAppId: string = useSelector(getFbAppId);
  const appleAppId: string = useSelector(getAppleAppId);
  const appleRedirectURI: string = useSelector(getAppleRedirectUri);

  const handleFacebookLogin = (payload: FacebookAuthResponse) => {
    // case 3
    // https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus/
    if (payload.status === FacebookAuthResponseStatus.unknown) return;
    dispatch(
      loginWithFacebook.started({
        referer,
        accessToken: payload.accessToken,
        userId: payload.userID,
        addNaturalCompany,
      }),
    );
  };

  const handleGoogleLogin = (accessToken: string) => {
    dispatch(
      loginWithGoogle.started({
        accessToken,
        referer,
        addNaturalCompany,
      }),
    );
  };

  const handleAppleLogin = (payload: AppleAuthResponse & { loginEnv: AppleLoginType }) => {
    const { authorization, user, loginEnv } = payload;
    const name = user?.name && { firstName: user?.name.firstName, lastName: user?.name.lastName };

    dispatch(
      loginWithApple.started({
        code: authorization.code,
        email: user?.email,
        name,
        referer,
        loginEnv,
        addNaturalCompany,
      }),
    );
  };

  const handleNativeMessages = (event: MessageEvent) => {
    const { type, data } = event.data;

    switch (type) {
      case WebViewMessagesTypes.loginFacebookSuccess:
        handleFacebookLogin(data);
        break;
      case WebViewMessagesTypes.loginGoogleSuccess:
        handleGoogleLogin(data.accessToken);
        break;
      case WebViewMessagesTypes.loginAppleSuccess:
        handleAppleLogin(data);
        break;
      default:
    }
  };

  useWebViewListener(handleNativeMessages);

  const renderFacebookLoginButton = () => {
    if (isMobileApp) {
      const mobileFacebookAuth = () => postMessageToWebView(WebViewMessagesTypes.loginFacebook);
      return <Button onClick={mobileFacebookAuth} />;
    }
    return (
      <FacebookLogin
        icon={null}
        appId={fbAppId}
        fields="name,email,picture"
        textButton=""
        scope="public_profile,email"
        callback={handleFacebookLogin}
      />
    );
  };

  const renderAppleLoginButton = () => {
    if (isMobileApp) {
      const mobileAppleAuth = () => postMessageToWebView(WebViewMessagesTypes.loginApple);
      return <Button onClick={mobileAppleAuth} />;
    }

    return (
      <AppleLogin
        scope="name email"
        clientId={appleAppId}
        redirectURI={appleRedirectURI}
        callback={data => {
          handleAppleLogin({ ...data, loginEnv: AppleLoginType.Web });
        }}
        usePopup
      />
    );
  };

  const googleLogin = useGoogleLogin({
    onSuccess: tokenResponse => {
      handleGoogleLogin(tokenResponse.access_token);
    },
  });

  return (
    <div className={styles.socialLogins}>
      {config.NODE_ENV !== NodeEnvEnum.production && (
        <div className={styles.button}>
          <Icon icon="fb-button" size={IconSizes.s} />
          <Typography tag="span" className={styles.buttonLabel} msg={t(Messages.loginWithFacebook)} />
          <div className={styles.buttonActionContainer}>{renderFacebookLoginButton()}</div>
        </div>
      )}
      <button
        className={styles.button}
        type="button"
        onClick={() => {
          if (!isMobileApp) {
            googleLogin();
          }
          postMessageToWebView(WebViewMessagesTypes.loginGoogle);
        }}
      >
        <Icon icon="google-button" size={IconSizes.s} />
        <Typography tag="span" className={styles.buttonLabel} msg={t(Messages.loginWithGoogle)} />
      </button>
      <div className={styles.button}>
        <Icon icon="apple-button" size={IconSizes.s} />
        <Typography tag="span" className={styles.buttonLabel} msg={t(Messages.loginWithApple)} />
        <div className={styles.buttonActionContainer}>{renderAppleLoginButton()}</div>
      </div>
    </div>
  );
};

export default SocialLogins;
